import * as Yup from 'common/yup';
import i18n from '../../i18n';
import {AzureStorageType} from '../AzureStorageType';
import {TransportType} from '../TransportType';
import {ShareFileFolderType} from '../ShareFileFolderType';
import {ServerTypeName} from 'common/enums';
import {serverTypeNameSchema} from 'types/transport/ServerTypeName';
import {GoogleCloudHealthDataType} from './enums/GoogleCloudHealthDataType';
import {ContentStructure} from './enums/ContentStructure';
import {AbstractServer, portSchema} from './Abstract/AbstractServer';
import {passphrase} from 'common/yup/customs/passphrase';

/**
 * Amazon S3 server
 */
export const amazonS3ServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.AMAZONS3),
   secretKey: passphrase(),
   bucket: Yup.string(),
   encrypt: Yup.bool(),

   // The following were added for 8.3 for "compatible" endpoints
   s3Compatible: Yup.boolean(),
   s3PathStyleAccess: Yup.bool(),
   endpoint: Yup.string(),
   region: Yup.string(),

   bucketOwnerFullControl: Yup.bool(),
   expectBucketOwner: Yup.bool(),
   expectedBucketOwner: Yup.string(),
});

export type AmazonS3Server = Yup.InferType<typeof amazonS3ServerSchema>;

/**
 * AS2 server
 */
export const aS2ServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.AS2),
   address: Yup.string().required(i18n.t('URL for the AS2 server must be supplied')),
   /** DB ID of Server Certificate to use for AS2 **/
   sslServerCertId: Yup.idString().required(i18n.t('You must supply a Server SSL Certificate.')),

   /** DB ID of Client Certificate to use with this AS2 transfer **/
   sslClientCertId: Yup.idString().required(i18n.t('You must supply a Client SSL Certificate.')),

   /** Subject of the AS2 Message **/
   subject: Yup.string(),

   /** AS2-ID of the partner **/
   recipientAS2Id: Yup.idString().required(i18n.t('You must specify an AS2 Recipient ID.')),

   /** AS2-ID of sender (Diplomat) **/
   senderAS2Id: Yup.idString().required(i18n.t('You must specify an AS2 Sender ID.')), // TODO: AS2: global parameter,

   /** AS2 "from" email address */
   email: Yup.string().email(),

   /** File Type (Ascii/Binary) of the partner **/
   // FileType fileType;

   /** Compress the message? **/
   compress: Yup.boolean(),

   /** Encryption algorithm for the message **/
   encryptionAlgorithm: Yup.string().default('aes256-cbc'),

   /** Signing algorithm for the message **/
   signingAlgorithm: Yup.string().default('sha-256'),
});
export type AS2Server = Yup.InferType<typeof aS2ServerSchema>;

/**
 * AZURE STORAGE server
 */
export const azureStorageServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.AZURESTORAGE),
   account: Yup.string(),
   key: Yup.string(),
   share: Yup.string(),
   storageType: Yup.string().oneOf(AzureStorageType.keys).default(AzureStorageType.enum.FILE),
   useSas: Yup.bool(),
   sas: Yup.string(),
});
export type azureStorageServer = Yup.InferType<typeof azureStorageServerSchema>;

/**
 * Box server
 */
export const boxServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.BOX),
   credentials: passphrase(),
   userId: Yup.idString(),
});

export type BoxServer = Yup.InferType<typeof boxServerSchema>;

/**
 * Remote Agent server
 */
export const cloudServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.REMOTE),
   address: Yup.string().required(i18n.t('You must specify Address')),
   /** DB ID of Diplomat server's key.*/
   serverKeyId: Yup.idString().required(i18n.t('You must specify MFT Site key')),
   // serverKey: DiplomatSecretKey,
   /** Should data be encrypted between cloud and Diplomat? */
   encryptTransmission: Yup.boolean(),
   /** Should checkpoint/restart be attempted on transmission failure? */
   checkpointRestart: Yup.boolean().default(true),
   port: portSchema()
      .default(TransportType.CLOUD.defaultInputPort)
      .required(i18n.t('You must specify Port')),

   numRetries: Yup.number().default(1),
   retryDelay: Yup.number().default(30),
   bufferSize: Yup.number().default(100),
   bandwidth: Yup.number().default(0),
});

export type CloudServer = Yup.InferType<typeof cloudServerSchema>;

/**
 * Dropbox server
 */
export const DropboxServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.DROPBOX),
});
export type DropboxServer = Yup.InferType<typeof DropboxServerSchema>;

/**
 * Email server
 */
// export const EmailServerSchema = serverSchema.shape({

// });
// export type EmailServer = Yup.InferType<typeof EmailServerSchema>;

/**
 * Local server
 */
export const LocalServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.LOCAL),
   directory: Yup.string().required(i18n.t('Local network directory cannot be blank')),
});
export type LocalServer = Yup.InferType<typeof LocalServerSchema>;

/**
 * Ftp server
 */
export const FtpServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.FTP),
   address: Yup.string().required(i18n.t('Must be supplied')).defined(),
   port: portSchema()
      .required(i18n.t('Must be supplied'))
      .default(TransportType.FTP.defaultInputPort),
   username: Yup.string().required(i18n.t('Must be supplied')).defined(),
   password: passphrase().required(i18n.t('Must be supplied')).defined(),

   /** Account to use on server.*/
   account: Yup.string(),
   /** Should FTP server interaction be in passive mode (true) or not (false)?*/
   passiveMode: Yup.boolean().defined().default(true),
   // _mode: Yup.string().oneOf(ServerModes.keys),
   /** Value of SITE command to be issued after connection - ftp and ftps only */
   siteCommand: Yup.string(),
});

export type FtpServer = Yup.InferType<typeof FtpServerSchema>;

/**
 * Ftps server
 */
export const FtpsServerSchema = FtpServerSchema.shape({
   /** Should SSL FTP server interaction be done in implicit mode?*/
   implicitSsl: Yup.boolean().default(false),
   /** DB ID of Server Certificate to expect with SSL **/
   sslCertId: Yup.idString(),
   /** Should SSL FTP server use clear command channel?*/
   ccc: Yup.boolean(),
});
export type FtpsServer = Yup.InferType<typeof FtpsServerSchema>;

const sftpKeyPassRequired = (other: string, schema: Yup.StringSchema) =>
   other ? schema : schema.required('Password or SSH Key for SFTP server must be supplied');
/**
 * Sftp server
 */
export const SftpServerSchema = FtpServerSchema.shape({
   /** DB ID of SSH key.*/
   sshKeyId: Yup.idString(),
   hostVerification: Yup.boolean(),
   password: passphrase().when('sshKeyId', sftpKeyPassRequired),
   port: portSchema()
      .required(i18n.t('Must be supplied'))
      .default(TransportType.SFTP.defaultInputPort),
});
export type SftpServer = Yup.InferType<typeof SftpServerSchema>;

/**
 * GoogleCloud server
 */
export const GoogleCloudServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.GOOGLECLOUD),
   credentials: passphrase(),
   bucket: Yup.string(),
   importCloudHealthDataAfterUpload: Yup.boolean(),
   cloudHealthDataType: Yup.originString().oneOf(GoogleCloudHealthDataType.keys),
   projectId: Yup.idString(),
   dataSetId: Yup.idString(),
   contentStructure: Yup.originString().oneOf(ContentStructure.keys),
   storeId: Yup.idString(),
});
export type GoogleCloudServer = Yup.InferType<typeof GoogleCloudServerSchema>;

/**
 * Http server
 */
export const HttpServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.HTTP),
   address: Yup.string().required(i18n.t('Must be supplied')).defined(),
   port: portSchema()
      .required(i18n.t('Must be supplied'))
      .default(TransportType.HTTP.defaultInputPort),
});
export type HttpServer = Yup.InferType<typeof HttpServerSchema>;

/**
 * Https server
 */
export const HttpsServerSchema = HttpServerSchema.shape({
   port: portSchema()
      .required(i18n.t('Must be supplied'))
      .default(TransportType.HTTPS.defaultInputPort),
});
export type HttpsServer = Yup.InferType<typeof HttpsServerSchema>;

/**
 * OracleCloud server
 */
export const OracleCloudServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.ORACLECLOUD),
   serviceName: Yup.string(),
   identityDomain: Yup.string(),
   serviceUrl: Yup.string(),
   container: Yup.string(),
});
export type OracleCloudServer = Yup.InferType<typeof OracleCloudServerSchema>;

/**
 * ShareFile server
 */
export const ShareFileServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.SHAREFILE),
   clientId: Yup.idString(),
   clientSecret: passphrase(),
   folderType: Yup.string().oneOf(ShareFileFolderType.keys),
   sharedFolderName: Yup.string().when('folderType', {
      is: ShareFileFolderType.enum.SHARED,
      then: Yup.string().required(i18n.t('Shared folder name must be specified')),
   }),
});
export type ShareFileServer = Yup.InferType<typeof ShareFileServerSchema>;

/**
 * SMB server
 */
export const SMBServerSchema = AbstractServer.shape({
   typeName: serverTypeNameSchema.default(ServerTypeName.SMB),
   address: Yup.string().required(i18n.t('Must be supplied')).defined(),
   port: portSchema()
      .required(i18n.t('Must be supplied'))
      .default(TransportType.SMB.defaultInputPort),
   username: Yup.string().required(i18n.t('Must be supplied')).defined(),
   password: passphrase().required(i18n.t('Must be supplied')).defined(),
   share: Yup.string().required(i18n.t('Must be supplied')).defined(),
});
export type SMBServer = Yup.InferType<typeof SMBServerSchema>;
